import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, ExternalLink, Bell } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClaimBox, PlanCard, Locked } from "@/components/pro/ProBasics";
import { InfoEditor, TagEditor, ContactEditor } from "@/components/pro/InfoEditors";
import { ProductsEditor, PhotosEditor, ProReviews } from "@/components/pro/CatalogEditors";
import { InviteReview } from "@/components/pro/InviteReview";

function NewReviewsBanner({ count, onOpen }) {
  return (
    <button onClick={onOpen} className="flex w-full items-center gap-3 rounded-xl border border-[#D97706] bg-[#FEF3C7] p-4 text-left text-sm font-semibold text-[#78350F] transition-colors hover:bg-[#FDE68A]" data-testid="new-reviews-banner">
      <Bell className="h-5 w-5 shrink-0" />
      {count} nouvel{count > 1 ? "s" : ""} avis sur votre fiche — cliquez pour {count > 1 ? "les" : "le"} lire
    </button>
  );
}

function BusinessManager({ b, reload, initialTab }) {
  const ess = b.plan_active;
  const pro = b.plan_active && b.plan_raw === "pro";
  const [tab, setTab] = useState(initialTab || "info");
  const [cleared, setCleared] = useState(false);
  const newSince = useRef(b.reviews_seen_at);
  useEffect(() => {
    if (tab === "reviews" && b.new_reviews > 0 && !cleared) {
      setCleared(true);
      api.post(`/pro/businesses/${b.id}/reviews/seen`).then(() => window.dispatchEvent(new Event("qfq:notif-refresh"))).catch(() => {});
    }
  }, [tab, b.id, b.new_reviews, cleared]);
  return (
    <div className="space-y-6" data-testid={`manager-${b.id}`}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold" data-testid="manager-business-name">{b.name}</h2>
          <p className="font-mono-id text-xs text-slate-500">SIRET {b.siret} · {b.address}</p>
        </div>
        <Link to={`/entreprise/${b.id}`} className="inline-flex items-center gap-1 text-sm font-semibold text-[#1E40AF] hover:underline" data-testid="view-public-page-link">Voir ma fiche publique<ExternalLink className="h-4 w-4" /></Link>
      </div>
      {b.new_reviews > 0 && !cleared && <NewReviewsBanner count={b.new_reviews} onOpen={() => { setTab("reviews"); setTimeout(() => document.getElementById("pro-tabs")?.scrollIntoView({ behavior: "smooth", block: "start" }), 100); }} />}
      <PlanCard b={b} onChange={reload} />
      <Tabs value={tab} onValueChange={setTab} id="pro-tabs" className="scroll-mt-20">
        <TabsList className="flex h-auto flex-wrap justify-start">
          <TabsTrigger value="info" data-testid="tab-info">Infos</TabsTrigger>
          <TabsTrigger value="brands" data-testid="tab-brands">Marques & compétences</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">Produits & stock</TabsTrigger>
          <TabsTrigger value="photos" data-testid="tab-photos">Photos</TabsTrigger>
          <TabsTrigger value="reviews" data-testid="tab-reviews">Avis & invitations{b.new_reviews > 0 && !cleared && <span className="ml-1.5 rounded-full bg-[#D97706] px-1.5 text-[11px] font-bold text-white" data-testid="tab-reviews-new-count">{b.new_reviews}</span>}</TabsTrigger>
        </TabsList>
        <TabsContent value="info" className="mt-4 space-y-4"><ContactEditor b={b} onSaved={reload} />{ess ? <InfoEditor b={b} onSaved={reload} /> : <Locked need="essentiel" />}</TabsContent>
        <TabsContent value="brands" className="mt-4 space-y-4">
          {ess ? (
            <>
              <TagEditor b={b} field="brands" title="Marques que vous représentez" placeholder="ex. Bosch, Miele, Whirlpool" onSaved={reload} />
              <TagEditor b={b} field="skills" title="Vos compétences" placeholder="ex. Réparation lave-linge" onSaved={reload} />
            </>
          ) : <Locked need="essentiel" />}
        </TabsContent>
        <TabsContent value="products" className="mt-4">{pro ? <ProductsEditor b={b} onSaved={reload} /> : <Locked need="pro" />}</TabsContent>
        <TabsContent value="photos" className="mt-4">{pro ? <PhotosEditor b={b} onSaved={reload} /> : <Locked need="pro" />}</TabsContent>
        <TabsContent value="reviews" className="mt-4 space-y-4"><InviteReview b={b} /><ProReviews bid={b.id} newSince={newSince.current} /></TabsContent>
      </Tabs>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const [list, setList] = useState(null);
  const [selected, setSelected] = useState(null);
  const load = useCallback(async (focus) => {
    const { data } = await api.get("/pro/businesses");
    setList(data);
    setSelected((s) => focus || s || data[0]?.id || null);
  }, []);
  useEffect(() => {
    load(params.get("entreprise")).catch(() => setList([]));
  }, [load, params.get("entreprise")]); // eslint-disable-line react-hooks/exhaustive-deps
  const current = list?.find((b) => b.id === selected);
  return (
    <div className="mx-auto max-w-6xl px-4 py-10 sm:px-6 lg:px-8" data-testid="dashboard-page">
      <p className="text-xs font-bold uppercase tracking-wider text-[#1E40AF]">Mon espace</p>
      <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tight sm:text-4xl">Bonjour {user?.name?.split(" ")[0]}</h1>
      <p className="mt-2 text-slate-600">Particulier : notez les entreprises depuis leur fiche. Professionnel : revendiquez et complétez votre fiche ici.</p>
      <div className="mt-8"><ClaimBox initial={params.get("siret") || ""} onClaimed={(id) => load(id)} /></div>
      {list === null && <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>}
      {list?.length > 1 && (
        <div className="mt-8 flex flex-wrap gap-2" data-testid="my-businesses-list">
          {list.map((b) => (
            <button key={b.id} onClick={() => setSelected(b.id)} className={`rounded-full border px-4 py-2 text-sm font-semibold ${b.id === selected ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white hover:border-[#1E40AF]"}`} data-testid={`select-business-${b.id}`}>{b.name}</button>
          ))}
        </div>
      )}
      {current && <div className="mt-8"><BusinessManager key={current.id + (current.plan_until || "") + params.toString()} b={current} reload={() => load()} initialTab={params.get("onglet") === "avis" ? "reviews" : "info"} /></div>}
    </div>
  );
}
